// Cloudflare Workers版 SystemA — window.api 互換 fetch ラッパー
// build 時に環境変数 VITE_API_BASE を指定可(本番は同一オリジン想定で空)

const BASE = (import.meta as any).env?.VITE_API_BASE || '';

async function req(method: string, path: string, body?: any): Promise<any> {
  const opts: RequestInit = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  };
  const res = await fetch(`${BASE}/api${path}`, opts);
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); msg = (j as any)?.error || msg; } catch {}
    throw new Error(msg);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return await res.json();
  if (ct.includes('application/pdf')) return await res.blob();
  return await res.text();
}

const get = (p: string) => req('GET', p);
const post = (p: string, b?: any) => req('POST', p, b);
const put = (p: string, b?: any) => req('PUT', p, b);
const del = (p: string) => req('DELETE', p);

// PDF表示用ヘルパー: blob を新タブで開く
async function openPdf(path: string) {
  const blob = await req('GET', path);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const api = {
  invoke: async (channel: string, ...args: any[]) => {
    // channel = "customer:list" → "/customer" (簡易マップ)
    return get(`/${channel.split(':')[0]}`);
  },
  customer: {
    list: (params?: any) => get(`/customer${params?.q ? `?q=${encodeURIComponent(params.q)}` : ''}`),
    get: (id: number) => get(`/customer/${id}`),
    create: (data: any) => post('/customer', data),
    update: (id: number, data: any) => put(`/customer/${id}`, data),
    delete: (id: number) => del(`/customer/${id}`),
    search: (q: string) => get(`/customer?q=${encodeURIComponent(q)}`),
  },
  item: {
    list: () => get('/item'),
    get: (id: number) => get(`/item/${id}`),
    create: (data: any) => post('/item', data),
    update: (id: number, data: any) => put(`/item/${id}`, data),
    delete: (id: number) => del(`/item/${id}`),
  },
  item_master: { list: () => get('/item'), get: (id: number) => get(`/item/${id}`) },
  estimate: {
    list: (params?: any) => get('/estimate'),
    get: (id: number) => get(`/estimate/${id}`),
    create: (data: any) => post('/estimate', data),
    update: (id: number, data: any) => put(`/estimate/${id}`, data),
    delete: (id: number) => del(`/estimate/${id}`),
    generatePdf: (id: number) => openPdf(`/pdf/estimate/${id}`),
    duplicate: (id: number) => post(`/estimate/${id}/duplicate`),
    changeStatus: (id: number, status: string) => put(`/estimate/${id}/status`, { status }),
  },
  order: {
    list: () => get('/order'),
    get: (id: number) => get(`/order/${id}`),
    create: (data: any) => post('/order', data),
    update: (id: number, data: any) => put(`/order/${id}`, data),
    delete: (id: number) => del(`/order/${id}`),
    generatePdf: (id: number) => openPdf(`/pdf/order/${id}`),
    createFromEstimate: (estimateId: number) => post(`/order/from-estimate/${estimateId}`),
  },
  schedule: {
    list: (params?: any) => get(`/schedule/by-order/${params?.orderId || 0}`),
    get: (id: number) => get(`/schedule/by-order/${id}`),
    create: (data: any) => post('/schedule', data),
    update: (id: number, data: any) => post('/schedule', data),
    delete: (id: number) => del(`/schedule/${id}`),
    generateFromOrder: (orderId: number) => post(`/schedule/generate/${orderId}`),
    bulkUpdate: (tasks: any[]) => post('/schedule', { tasks }),
    generateGantt: (id: number) => openPdf(`/pdf/schedule/${id}`),
  },
  delivery: {
    list: () => get('/delivery'),
    get: (id: number) => get(`/delivery/${id}`),
    create: (data: any) => post('/delivery', data),
    update: (id: number, data: any) => put(`/delivery/${id}`, data),
    delete: (id: number) => del(`/delivery/${id}`),
    generatePdf: (id: number) => openPdf(`/pdf/delivery/${id}`),
    fromOrder: (orderId: number) => post(`/delivery/from-order/${orderId}`),
  },
  invoice: {
    list: () => get('/invoice'),
    get: (id: number) => get(`/invoice/${id}`),
    create: (data: any) => post('/invoice', data),
    update: (id: number, data: any) => put(`/invoice/${id}`, data),
    delete: (id: number) => del(`/invoice/${id}`),
    generatePdf: (id: number) => openPdf(`/pdf/invoice/${id}`),
    fromOrder: (orderId: number) => post(`/invoice/from-order/${orderId}`),
    recordPayment: (id: number, amount: number, date: string, note: string) =>
      post(`/invoice/${id}/payment`, { amount, date, note }),
  },
  setting: {
    get: (key: string) => get('/setting').then((s: any) => s[key]),
    set: (key: string, value: any) => put('/setting', { [key]: value }),
    getAll: () => get('/setting'),
    update: (key: string, value: any) => put('/setting', { [key]: value }),
    updateAll: (data: any) => put('/setting', data),
    selectStamp: async () => {
      // Web版ではファイル入力ダイアログ
      return new Promise<any>((resolve) => {
        const i = document.createElement('input');
        i.type = 'file';
        i.accept = 'image/*';
        i.onchange = async () => {
          const f = i.files?.[0];
          if (!f) return resolve(null);
          const fd = new FormData();
          fd.append('stamp', f);
          const res = await fetch(`${BASE}/api/setting/stamp`, { method: 'POST', body: fd });
          resolve(await res.json());
        };
        i.click();
      });
    },
  },
  pdf: {
    generate: (type: string, id: number) => openPdf(`/pdf/${type}/${id}`),
    openFolder: () => alert('Web版ではPDFは新タブで開きます'),
    estimate: (id: number) => openPdf(`/pdf/estimate/${id}`),
    order: (id: number) => openPdf(`/pdf/order/${id}`),
    delivery: (id: number) => openPdf(`/pdf/delivery/${id}`),
    invoice: (id: number) => openPdf(`/pdf/invoice/${id}`),
    schedule: (orderId: number) => openPdf(`/pdf/schedule/${orderId}`),
  },
};

// window.api を上書き(Electron版は preload.ts で既にセット・Web版はここで)
if (typeof window !== 'undefined' && !(window as any).api) {
  (window as any).api = api;
}

export default api;
